// https://www.radix-ui.com/primitives/docs/components/select
import * as SelectPrimitive from "@radix-ui/react-select";
import { useState, type ReactNode } from "react";
import cx from "ui/cx";
import { PopoverContainerContext } from "ui/hooks/usePopoverContainer";
import IconCheck from "ui/icons/IconCheck";
import IconMiniChevronDown from "ui/icons/IconMiniChevronDown";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import styles from "./SimpleSelect.module.scss";

interface SelectRootProps {
  children: ReactNode;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  dir?: "ltr" | "rtl";
  name?: string;
  disabled?: boolean;
  required?: boolean;
}

interface SelectTriggerProps {
  children?: ReactNode;
  className?: string;
  asChild?: boolean;
  placeholder?: string;
  id?: string;
}

interface SelectValueProps {
  children?: ReactNode;
  className?: string;
  placeholder?: string;
  asChild?: boolean;
}

interface SelectIconProps {
  children?: ReactNode;
  className?: string;
  asChild?: boolean;
}

interface SelectPortalProps {
  children: ReactNode;
  container?: HTMLElement;
}

interface SelectContentProps {
  children: ReactNode;
  className?: string;
  onCloseAutoFocus?: (event: Event) => void;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  onPointerDownOutside?: (event: CustomEvent<{ originalEvent: PointerEvent }>) => void;
  position?: "item-aligned" | "popper";
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  align?: "start" | "center" | "end";
  alignOffset?: number;
  avoidCollisions?: boolean;
  collisionBoundary?: Element | null | Array<Element | null>;
  collisionPadding?: number | Partial<Record<"top" | "right" | "bottom" | "left", number>>;

  sticky?: "partial" | "always";
  hideWhenDetached?: boolean;
  constrainWidth?: boolean; // sets width to trigger width
}

interface SelectViewportProps {
  children: ReactNode;
  className?: string;
  asChild?: boolean;
}

interface SelectItemProps {
  children: ReactNode;
  className?: string;
  value: string;
  disabled?: boolean;
  textValue?: string;
  asChild?: boolean;
  tooltip?: ReactNode;
}

interface SelectItemTextProps {
  children: ReactNode;
  className?: string;
  asChild?: boolean;
}

interface SelectItemIndicatorProps {
  children?: ReactNode;
  className?: string;
  asChild?: boolean;
}

interface SelectGroupProps {
  children: ReactNode;
  className?: string;
  asChild?: boolean;
}

interface SelectLabelProps {
  children: ReactNode;
  className?: string;
  asChild?: boolean;
}

interface SelectSeparatorProps {
  className?: string;
  asChild?: boolean;
}

// SelectBasic interfaces
interface SelectBasicItem {
  type: "item";
  value: string;
  label: ReactNode;
  disabled?: boolean;
  tooltip?: ReactNode;
}

interface SelectBasicGroup {
  type: "group";
  label: ReactNode;
  items: SelectBasicItem[];
}

interface SelectBasicSeparator {
  type: "separator";
}

type SelectBasicItemType = SelectBasicItem | SelectBasicGroup | SelectBasicSeparator;

interface SelectBasicProps
  extends Pick<SelectRootProps, "value" | "defaultValue" | "onValueChange" | "disabled" | "required" | "name">,
    Pick<SelectTriggerProps, "placeholder" | "id">,
    Pick<SelectContentProps, "side" | "sideOffset" | "align" | "alignOffset" | "constrainWidth"> {
  items: SelectBasicItemType[];
  className?: string;
  contentClassName?: string;
  triggerClassName?: string;
}

function SelectRoot({ children, ...props }: SelectRootProps) {
  return <SelectPrimitive.Root {...props}>{children}</SelectPrimitive.Root>;
}

function SelectTrigger({ children, className, placeholder, ...props }: SelectTriggerProps) {
  return (
    <SelectPrimitive.Trigger
      className={cx(!props.asChild ? styles.trigger : undefined, className)}
      data-testid="simple-select-trigger"
      {...props}
    >
      {children || (
        <>
          <SelectValue placeholder={placeholder} />
          <SelectIcon>
            <IconMiniChevronDown />
          </SelectIcon>
        </>
      )}
    </SelectPrimitive.Trigger>
  );
}

function SelectValue({ className, ...props }: SelectValueProps) {
  return <SelectPrimitive.Value className={cx(styles.value, className)} data-testid="simple-select-value" {...props} />;
}

function SelectIcon({ children, className, ...props }: SelectIconProps) {
  return (
    <SelectPrimitive.Icon className={cx(styles.icon, className)} data-testid="simple-select-icon" {...props}>
      {children || <IconMiniChevronDown />}
    </SelectPrimitive.Icon>
  );
}

function SelectPortal({ children, ...props }: SelectPortalProps) {
  return <SelectPrimitive.Portal {...props}>{children}</SelectPrimitive.Portal>;
}

function SelectContent({
  children,
  className,
  side = "bottom",
  sideOffset = 4,
  position = "popper",
  collisionPadding = 12,
  hideWhenDetached = true,
  constrainWidth = false,
  ...props
}: SelectContentProps) {
  return (
    <SelectPrimitive.Content
      className={cx(styles.content, constrainWidth && styles.constrainWidth, className)}
      side={side}
      sideOffset={sideOffset}
      position={position}
      collisionPadding={collisionPadding}
      hideWhenDetached={hideWhenDetached}
      role="listbox" // Used by Modal to determine if the click is inside the modal
      data-testid="simple-select-content"
      {...props}
    >
      {children}
    </SelectPrimitive.Content>
  );
}

function SelectViewport({ children, className, ...props }: SelectViewportProps) {
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);

  return (
    <PopoverContainerContext.Provider value={contentElement}>
      <SelectPrimitive.Viewport
        ref={setContentElement}
        className={cx(styles.viewport, className)}
        data-testid="simple-select-viewport"
        {...props}
      >
        {children}
      </SelectPrimitive.Viewport>
    </PopoverContainerContext.Provider>
  );
}

function SelectItem({ children, className, tooltip, ...props }: SelectItemProps) {
  const item = (
    <SelectPrimitive.Item
      className={cx(styles.item, className)}
      title={!tooltip && typeof children === "string" ? children : undefined}
      data-testid="simple-select-item"
      {...props}
    >
      <SelectItemText>{children}</SelectItemText>
      <SelectItemIndicator>
        <IconCheck />
      </SelectItemIndicator>
    </SelectPrimitive.Item>
  );

  return wrapWithTooltip(item, tooltip);
}

function SelectItemText({ children, className, ...props }: SelectItemTextProps) {
  return (
    <SelectPrimitive.ItemText {...props} asChild>
      <span className={cx(styles.itemText, className)}>{children}</span>
    </SelectPrimitive.ItemText>
  );
}

function SelectItemIndicator({ children, className, ...props }: SelectItemIndicatorProps) {
  return (
    <SelectPrimitive.ItemIndicator className={cx(styles.itemIndicator, className)} {...props}>
      {children || <IconCheck />}
    </SelectPrimitive.ItemIndicator>
  );
}

function SelectGroup({ children, className, ...props }: SelectGroupProps) {
  return (
    <SelectPrimitive.Group className={cx(styles.group, className)} data-testid="simple-select-group" {...props}>
      {children}
    </SelectPrimitive.Group>
  );
}

function SelectLabel({ children, className, ...props }: SelectLabelProps) {
  return (
    <SelectPrimitive.Label className={cx(styles.label, className)} data-testid="simple-select-label" {...props}>
      {children}
    </SelectPrimitive.Label>
  );
}

function SelectSeparator({ className, ...props }: SelectSeparatorProps) {
  return (
    <SelectPrimitive.Separator
      className={cx(styles.separator, className)}
      data-testid="simple-select-separator"
      {...props}
    />
  );
}

function SelectBasic({
  items,
  value,
  defaultValue,
  onValueChange,
  placeholder,
  disabled,
  required,
  name,
  className,
  contentClassName,
  triggerClassName,
  side = "bottom",
  sideOffset = 4,
  align = "start",
  alignOffset = 0,
  constrainWidth = false,
  id,
}: SelectBasicProps) {
  const renderItems = (itemList: SelectBasicItemType[]): ReactNode[] => {
    return itemList.map((item, index) => {
      switch (item.type) {
        case "item":
          return (
            <SelectItem
              key={`item-${item.value}-${index}`}
              value={item.value}
              disabled={item.disabled}
              tooltip={item.tooltip}
            >
              {item.label}
            </SelectItem>
          );

        case "group":
          return (
            <SelectGroup key={`group-${index}`}>
              <SelectLabel>{item.label}</SelectLabel>
              {renderItems(item.items)}
            </SelectGroup>
          );

        case "separator":
          return <SelectSeparator key={`separator-${index}`} />;

        default:
          return null;
      }
    });
  };

  return (
    <SelectRoot
      value={value}
      defaultValue={defaultValue}
      onValueChange={!disabled ? onValueChange : undefined}
      disabled={disabled}
      required={required}
      name={name}
    >
      <SelectTrigger className={cx(triggerClassName, className)} placeholder={placeholder} id={id} />
      <SelectPortal>
        <SelectContent
          className={contentClassName}
          side={side}
          sideOffset={sideOffset}
          align={align}
          alignOffset={alignOffset}
          constrainWidth={constrainWidth}
        >
          <SelectViewport>{renderItems(items)}</SelectViewport>
        </SelectContent>
      </SelectPortal>
    </SelectRoot>
  );
}

const SimpleSelect = {
  Root: SelectRoot,
  Trigger: SelectTrigger,
  Value: SelectValue,
  Icon: SelectIcon,
  Portal: SelectPortal,
  Content: SelectContent,
  Viewport: SelectViewport,
  Item: SelectItem,
  ItemText: SelectItemText,
  ItemIndicator: SelectItemIndicator,
  Group: SelectGroup,
  Label: SelectLabel,
  Separator: SelectSeparator,
  Basic: SelectBasic,
};

export default SimpleSelect;

export type {
  SelectRootProps,
  SelectTriggerProps,
  SelectValueProps,
  SelectIconProps,
  SelectPortalProps,
  SelectContentProps,
  SelectViewportProps,
  SelectItemProps,
  SelectItemTextProps,
  SelectItemIndicatorProps,
  SelectGroupProps,
  SelectLabelProps,
  SelectSeparatorProps,
  SelectBasicProps,
  SelectBasicItem,
  SelectBasicGroup,
  SelectBasicSeparator,
  SelectBasicItemType,
};
